import React from "react";
import * as FaIcons from "react-icons/fa";

const toolbarCardClass =
  "mb-4 w-full overflow-visible rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm";

const toolbarRowClass =
  "flex w-full flex-nowrap items-end justify-end gap-3 max-sm:grid max-sm:grid-cols-2 max-sm:items-end max-sm:gap-2";

const toolbarInlineClass =
  "flex w-full flex-nowrap items-center justify-end gap-3 max-sm:grid max-sm:grid-cols-2 max-sm:items-end max-sm:gap-2";

const searchWrapClass =
  "relative w-64 shrink-0 lg:w-80 max-sm:col-span-2 max-sm:w-full max-sm:min-w-0";

const searchInputClass =
  "h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-0 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const fieldInputClass =
  "h-9 w-full min-w-0 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const btnSecondaryClass =
  "inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 max-sm:flex-1";

const btnPrimaryClass =
  "inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md border-0 bg-blue-600 px-3 text-xs font-semibold text-white transition hover:bg-blue-700 max-sm:flex-1";

const fieldLabelClass =
  "text-[10px] font-semibold uppercase tracking-wide text-slate-500";

function ToolbarCard({
  children,
  onSubmit,
  className = "",
  withLabels = false,
}) {
  const cardClass = `${toolbarCardClass} ${className}`.trim();
  const innerClass = withLabels ? toolbarRowClass : toolbarInlineClass;

  const content = <div className={innerClass}>{children}</div>;

  if (onSubmit) {
    return (
      <form className={cardClass} onSubmit={onSubmit}>
        {content}
      </form>
    );
  }

  return <div className={cardClass}>{content}</div>;
}

export function ProviderSettingsInlineToolbar({ children, onSubmit, className = "" }) {
  return (
    <ToolbarCard onSubmit={onSubmit} className={className}>
      {children}
    </ToolbarCard>
  );
}

export function ProviderSettingsFilterToolbar({ children, onSubmit, className = "" }) {
  return (
    <ToolbarCard onSubmit={onSubmit} className={className} withLabels>
      {children}
    </ToolbarCard>
  );
}

export function ProviderSettingsToolbarActions({ children, className = "" }) {
  return (
    <div
      className={`flex shrink-0 flex-nowrap items-center gap-2 max-sm:col-span-2 max-sm:flex max-sm:w-full max-sm:justify-stretch ${className}`.trim()}
    >
      {children}
    </div>
  );
}

export function ProviderSettingsFilterField({
  label,
  htmlFor,
  children,
  className = "",
  wide = false,
}) {
  return (
    <div
      className={`flex w-36 shrink-0 flex-col gap-1 lg:w-40 max-sm:col-span-1 max-sm:w-full ${
        wide ? "w-64 lg:w-80 max-sm:col-span-2" : ""
      } ${className}`.trim()}
    >
      {label ? (
        <label htmlFor={htmlFor} className={fieldLabelClass}>
          {label}
        </label>
      ) : null}
      {children}
    </div>
  );
}

export function ProviderSettingsSearchInput({
  id,
  value,
  onChange,
  placeholder = "Search...",
  onKeyDown,
  className = "",
}) {
  return (
    <div className={`${searchWrapClass} ${className}`.trim()}>
      <input
        id={id}
        type="text"
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={searchInputClass}
      />
    </div>
  );
}

export function ProviderSettingsTextInput({ className = "", ...rest }) {
  return (
    <input {...rest} className={`${fieldInputClass} ${className}`.trim()} />
  );
}

export function ProviderSettingsSelect({ children, className = "", ...rest }) {
  return (
    <select {...rest} className={`${fieldInputClass} ${className}`.trim()}>
      {children}
    </select>
  );
}

export function ProviderSettingsButtonSecondary({
  children,
  type = "button",
  onClick,
  icon: Icon,
  className = "",
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`${btnSecondaryClass} ${className}`.trim()}
    >
      {Icon ? <Icon size={12} /> : null}
      {children}
    </button>
  );
}

export function ProviderSettingsButtonPrimary({
  children,
  type = "button",
  onClick,
  icon: Icon,
  className = "",
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`${btnPrimaryClass} ${className}`.trim()}
    >
      {Icon ? <Icon size={12} /> : null}
      {children}
    </button>
  );
}

export function ProviderSettingsAddButton({ children, onClick, className = "" }) {
  return (
    <ProviderSettingsButtonPrimary onClick={onClick} icon={FaIcons.FaPlus} className={className}>
      {children}
    </ProviderSettingsButtonPrimary>
  );
}
